/* eslint-disable react/no-inline-styles, no-inline-styles */
import React, { useState, useMemo, useEffect } from 'react';
import { youtubeGommeAdresi } from '../utils/youtube.js';
import { Student, ExamLibraryItem, TrialExamResult } from '../types';
import {
    ChartBarIcon,
    ExclamationIcon,
    PlusCircleIcon,
    XCircleIcon,
    TrendingUpIcon,
    BookOpenIcon,
    LightBulbIcon,
    FireIcon,
    CheckCircleIcon,
    DotsVerticalIcon,
    BookmarkIcon,
    ArrowRightIcon,
    GlobeIcon,
    ChevronRightIcon,
    UserIcon,
    CalculatorIcon,
    AcademicCapIcon,
    AwardIcon,
    ChatIcon,
    ClipboardListIcon,
    PrinterIcon,
    DocumentTextIcon,
    SearchIcon,
} from './icons';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { AddExamModal } from './AddExamModal';
import { analyzeStudentExams, Recommendation } from '../services/analysisService';
import { MOCK_TEST_LIBRARY } from '../data/mockData';
import { api, apiFetch } from '../services/api';

interface ExamTrackingViewProps {
    student: Student;
    examLibrary: ExamLibraryItem[];
    onUpdateStudent: (student: Student) => void;
}

const TYT_SUBJECTS = ['Türkçe', 'Matematik', 'Tarih', 'Coğrafya', 'Felsefe', 'Din Kültürü', 'Fizik', 'Kimya', 'Biyoloji'];
const AYT_SUBJECTS = ['Matematik', 'Edebiyat', 'Tarih-1', 'Coğrafya-1', 'Tarih-2', 'Coğrafya-2', 'Fizik', 'Kimya', 'Biyoloji'];
const LGS_SUBJECTS = ['Türkçe', 'Matematik', 'Fen Bilimleri', 'T.C. İnkılap Tarihi', 'Din Kültürü', 'Yabancı Dil'];

export const ExamTrackingView: React.FC<ExamTrackingViewProps> = ({ student, examLibrary, onUpdateStudent }) => {
    const [subTab, setSubTab] = useState<'TYT' | 'AYT' | 'YDT' | 'LGS'>('TYT');
    const [mainTab, setMainTab] = useState<'TRACKING' | 'ANALYSIS' | 'QUESTION_BANK' | 'MISTAKE_REPORT'>('TRACKING');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedLibraryExam, setSelectedLibraryExam] = useState<ExamLibraryItem | null>(null);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
    const [quizTitle, setQuizTitle] = useState("");
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isFlipped, setIsFlipped] = useState(false);
    const [selectedExamId, setSelectedExamId] = useState<string | null>(null);
    const [analysisExamFilter, setAnalysisExamFilter] = useState<'ALL' | 'TYT' | 'AYT'>('ALL');
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [selectedStatsLesson, setSelectedStatsLesson] = useState<string | null>(null);
    const [fetchedQuestionSets, setFetchedQuestionSets] = useState<any[]>([]);
    const [loadingBank, setLoadingBank] = useState(false);
    const [showBookletModal, setShowBookletModal] = useState(false);
    const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
    const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);
    const [quizStartTime, setQuizStartTime] = useState<number | null>(null);
    const [examSearchQuery, setExamSearchQuery] = useState("");
    const [adminExamResults, setAdminExamResults] = useState<any[]>([]);
    const [isLoadingResults, setIsLoadingResults] = useState(false);
    const [ranking, setRanking] = useState<{ rank: number, total: number } | null>(null);

    const fetchAdminResults = React.useCallback(async () => {
        if (!student.id) return;
        setIsLoadingResults(true);
        try {
            const response = await apiFetch(`/api/admin-exams/results/student/${student.id}`);
            if (response.ok) {
                const data = await response.json();
                setAdminExamResults(data);
            }
        } catch (err) {
            console.error("Failed to fetch admin results:", err);
        } finally {
            setIsLoadingResults(false);
        }
    }, [student.id]);

    useEffect(() => {
        fetchAdminResults();
    }, [fetchAdminResults]);

    useEffect(() => {
        if (student.id) {
            api.getRanking(student.id, subTab).then(setRanking).catch(console.error);
        }
    }, [student.id, subTab, adminExamResults]);

    const relevantExams = useMemo(() => {
        const manual = (student.trialExams || []).filter(e => e.examType === subTab);
        const admin = adminExamResults
            .filter(r => r.examId && r.examId.examType === subTab)
            .map(r => ({
                id: r._id,
                name: r.examId?.name || "Tanımlı Deneme",
                date: r.examId?.date || r.createdAt,
                examType: r.examId?.examType,
                scores: r.analysis.reduce((acc: any, curr: any) => {
                    if (!acc[curr.subject]) acc[curr.subject] = { correct: 0, incorrect: 0, empty: 0, net: 0 };
                    acc[curr.subject].correct += curr.correct;
                    acc[curr.subject].incorrect += curr.incorrect;
                    acc[curr.subject].empty += curr.blank;
                    const wrongImpact = subTab === 'LGS' ? (1 / 3) : 0.25;
                    acc[curr.subject].net += curr.correct - (curr.incorrect * wrongImpact);
                    return acc;
                }, {}),
                topicAnalysis: r.analysis.map((a: any) => ({
                    topic: a.topic,
                    subject: a.subject,
                    correct: a.correct,
                    incorrect: a.incorrect,
                    empty: a.blank,
                    net: a.correct - (a.incorrect * (subTab === 'LGS' ? (1 / 3) : 0.25))
                })),
                totalNet: r.net,
                totalCorrect: r.totalCorrect,
                totalIncorrect: r.totalIncorrect,
                totalEmpty: r.totalBlank,
                isAdminExam: true
            }));
        return [...manual, ...admin];
    }, [student.trialExams, subTab, adminExamResults]);

    // Use spread to avoid mutating the original array with sort
    const sortedRelevantExams = useMemo(() => {
        return [...relevantExams].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [relevantExams]);

    const chartData = [...relevantExams]
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .map(e => ({
            name: (e.name || "").length > 15 ? e.name.substring(0, 15) + '...' : (e.name || "Deneme"),
            fullDate: new Date(e.date).toLocaleDateString('tr-TR'),
            net: e.totalNet
        }));

    // Get the selected or last exam for summary card
    const lastExam = useMemo(() => {
        if (sortedRelevantExams.length === 0) return null;
        if (selectedExamId) {
            const found = relevantExams.find(e => e.id === selectedExamId);
            if (found) return found;
        }
        return sortedRelevantExams[0];
    }, [sortedRelevantExams, selectedExamId, relevantExams]);

    // List of exams sorted by date descending for the history/flip-card
    const sortedExams = sortedRelevantExams;

    const patterns = useMemo(() => {
        // Guard: At least 1 exam required for analysis
        if (sortedExams.length < 1) return [];

        const stats: Record<string, { incorrect: number, empty: number, total: number }> = {};
        // Analyze only the last 3 exams for recency
        sortedExams.slice(0, 3).forEach(e =>
            (e.topicAnalysis || []).filter(t => t.incorrect > 0 || t.empty > 0).forEach(t => {
                const key = `${t.subject} - ${t.topic}`;
                if (!stats[key]) stats[key] = { incorrect: 0, empty: 0, total: 0 };
                stats[key].incorrect += t.incorrect;
                stats[key].empty += t.empty;
                stats[key].total += (t.incorrect + t.empty);
            })
        );
        return Object.entries(stats).sort((a, b) => b[1].total - a[1].total).slice(0, 5);
    }, [sortedExams]);


    // Calculate subject-by-subject averages (shown after 3 exams)
    const subjectAverages = useMemo(() => {
        if (relevantExams.length < 3) return null;
        const subjectStats: Record<string, { total: number, count: number }> = {};

        relevantExams.forEach(exam => {
            Object.entries(exam.scores).forEach(([subject, data]: [string, { correct: number, incorrect: number, empty: number, net: number }]) => {
                if (!subjectStats[subject]) subjectStats[subject] = { total: 0, count: 0 };
                subjectStats[subject].total += data.net;
                subjectStats[subject].count += 1;
            });
        });

        const relevantSubjects = subTab === 'TYT' ? TYT_SUBJECTS : subTab === 'AYT' ? AYT_SUBJECTS : subTab === 'LGS' ? LGS_SUBJECTS : [];

        return Object.entries(subjectStats)
            .filter(([subject]) => relevantSubjects.length === 0 || relevantSubjects.includes(subject))
            .map(([subject, stats]) => ({
                subject,
                average: (stats.total / stats.count).toFixed(2),
                examCount: stats.count
            })).sort((a, b) => parseFloat(b.average) - parseFloat(a.average));
    }, [relevantExams, subTab]);

    const recommendations = useMemo(() => {
        // We might need to update analysisService to handle our merged structure or just pass the merged list
        // and let it treat them as TrialExamResults.
        const modifiedStudent = { ...student, trialExams: relevantExams };
        return analyzeStudentExams(modifiedStudent as any);
    }, [relevantExams, student]);

    // KPI Calculations
    const kpiData = useMemo(() => {
        // 1. Solving Speed (based on online exams with durationUsed)
        const examsWithDuration = relevantExams.filter(e => e.durationUsed && e.durationUsed > 0);
        let speedLabel = "0.0 dk";
        let speedDiff = "";
        const TYT_REF_SEC = 1.37 * 60; // 82.2 seconds

        if (examsWithDuration.length > 0) {
            const totalSeconds = examsWithDuration.reduce((acc, e) => acc + (e.durationUsed || 0), 0);
            const totalQuestions = examsWithDuration.reduce((acc, e) => acc + (e.totalCorrect + e.totalIncorrect + e.totalEmpty), 0);
            if (totalQuestions > 0) {
                const avgSecondsPerQuestion = totalSeconds / totalQuestions;
                speedLabel = `${(avgSecondsPerQuestion / 60).toFixed(2)} dk`;

                if (subTab === 'TYT') {
                    const diff = TYT_REF_SEC - avgSecondsPerQuestion;
                    if (diff > 0) {
                        speedDiff = `${(diff / 60).toFixed(2)} dk daha hızlı`;
                    } else if (diff < 0) {
                        speedDiff = `${(Math.abs(diff) / 60).toFixed(2)} dk daha yavaş`;
                    }
                } else if (subTab === 'LGS') {
                    const LGS_REF_SEC = 1.72 * 60; // 103.2 seconds
                    const diff = LGS_REF_SEC - avgSecondsPerQuestion;
                    if (diff > 0) {
                        speedDiff = `${(diff / 60).toFixed(2)} dk daha hızlı`;
                    } else if (diff < 0) {
                        speedDiff = `${(Math.abs(diff) / 60).toFixed(2)} dk daha yavaş`;
                    }
                }
            }
        }

        // 2. Success Score (Ham Puan & Yerleştirme Puanı)
        // Coefficients based on the approved plan
        const coeffs: Record<string, number> = {
            'Türkçe': 3.3,
            'Matematik': 3.3,
            'Sosyal Bilimler': 3.4,
            'Fen Bilimleri': 3.4
        };

        if (relevantExams.length === 0) {
            const baseBonus = subTab === 'LGS' ? 194.75 : 100;
            return { speed: "0.0 dk", rawScore: baseBonus.toFixed(1), placementScore: student.obp ? (baseBonus + student.obp * 0.6).toFixed(1) : baseBonus.toFixed(1) };
        }

        // Use the average of the last 3 exams or all if less than 3
        const recentExams = [...relevantExams].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);

        let totalHamPuan = 0;
        recentExams.forEach(exam => {
            let examHamPuan = subTab === 'LGS' ? 194.75 : 100; // Base score
            const currentCoeffs: Record<string, number> = subTab === 'LGS' ? {
                'Türkçe': 4,
                'Matematik': 4,
                'Fen Bilimleri': 4,
                'T.C. İnkılap Tarihi': 1,
                'Din Kültürü': 1,
                'Yabancı Dil': 1
            } : coeffs;

            Object.entries(exam.scores).forEach(([subject, data]: [string, any]) => {
                const coeff = currentCoeffs[subject] || (subTab === 'LGS' ? 1 : 3.3);
                examHamPuan += data.net * coeff;
            });
            totalHamPuan += Math.min(500, Math.max(subTab === 'LGS' ? 194.75 : 100, examHamPuan));
        });

        const avgHamPuan = totalHamPuan / recentExams.length;
        const obpFactor = subTab === 'LGS' ? 0 : 0.6; // OBP typically used in YKS not direct LGS score (LGS uses YBP sometimes but formula varies)
        const placementScore = avgHamPuan + (student.obp || 0) * obpFactor;

        return {
            speed: speedLabel,
            speedDiff: speedDiff,
            rawScore: avgHamPuan.toFixed(1),
            placementScore: placementScore.toFixed(1)
        };
    }, [relevantExams, student.obp]);

    const personalQuestionSets = useMemo(() => {
        const sets: { topic: string, subject: string, count: number, questions: any[] }[] = [];
        const libraryForExam = MOCK_TEST_LIBRARY[subTab] || {};
        patterns.forEach(([key, stat]) => {
            const [subject, topic] = key.split(' - ');
            if (libraryForExam[subject]) {
                const relevantTests = libraryForExam[subject].filter(test => test.title.toLowerCase().includes((topic || "").toLowerCase()));
                const pool = relevantTests.length > 0 ? relevantTests : libraryForExam[subject];
                if (pool && Array.isArray(pool) && pool.length > 0) {
                    const allQuestions = pool.flatMap(t => t.questions || []).slice(0, 10);
                    sets.push({ topic: topic || "Genel", subject, count: allQuestions.length, questions: allQuestions });
                }
            }
        });
        return sets;
    }, [patterns, subTab]);

    const startQuiz = (set: any) => {
        setQuizQuestions(set.questions);
        setQuizTitle(`${set.subject} - ${set.topic} Tarama`);
        setCurrentQuizId(set.quizId || null);
        setActiveQuestionIndex(0);
        setSelectedOption(null);
        setShowFeedback(false);
        setQuizAnswers([]);
        setQuizStartTime(Date.now());
        setShowQuizModal(true);
    };

    const handleAnswer = (idx: number) => {
        if (showFeedback) return;
        setSelectedOption(idx);
        setShowFeedback(true);
        setQuizAnswers(prev => [...prev, idx]);
    };

    const nextQuestion = async () => {
        if (activeQuestionIndex < quizQuestions.length - 1) {
            setActiveQuestionIndex(prev => prev + 1);
            setSelectedOption(null);
            setShowFeedback(false);
        } else {
            // Quiz finished, submit to server if possible
            if (currentQuizId) {
                try {
                    const timeSpent = quizStartTime ? Math.round((Date.now() - quizStartTime) / 1000) : 0;
                    await api.submitQuizResult(currentQuizId, {
                        studentId: student.id,
                        answers: quizAnswers,
                        timeSpentSeconds: timeSpent
                    });
                } catch (err) {
                    console.error("Failed to submit quiz", err);
                }
            }
            setShowQuizModal(false);
        }
    };

    const handleSaveExam = (newExam: TrialExamResult | any) => {
        // If it's an empty object, it means an AdminExam was saved in the modal
        if (!newExam || Object.keys(newExam).length === 0) {
            fetchAdminResults();
            setShowAddModal(false);
            return;
        }

        const updatedStudent = {
            ...student,
            trialExams: [...(student.trialExams || []), newExam]
        };
        onUpdateStudent(updatedStudent);
        setShowAddModal(false);
        setSelectedLibraryExam(null);
    };

    const openAddModal = (libraryExam?: ExamLibraryItem) => {
        if (libraryExam) {
            setSelectedLibraryExam(libraryExam);
            if (libraryExam.examType === 'TYT' || libraryExam.examType === 'AYT') setSubTab(libraryExam.examType);
        } else {
            setSelectedLibraryExam(null);
        }
        setShowAddModal(true);
    };

    const handleTabChange = (type: 'TYT' | 'AYT' | 'YDT') => {
        setSubTab(type);
        setMainTab('TRACKING'); // Her sınav tipi tıklandığında Dashboard'a dön
    };

    // Fetch mistaken questions when Question Bank tab is active OR booklet modal opens
    useEffect(() => {
        if (mainTab === 'QUESTION_BANK' || showBookletModal) {
            const fetchBank = async () => {
                try {
                    setLoadingBank(true);
                    const data = await api.getMistakenQuestions(student.id);
                    setFetchedQuestionSets(data || []);
                } catch (err) {
                    console.error("Failed to fetch question bank", err);
                    setFetchedQuestionSets([]);
                } finally {
                    setLoadingBank(false);
                }
            };
            fetchBank();
        }
    }, [mainTab, student.id, showBookletModal]);

    // Combine API data with pattern-based questions as fallback
    // If API returns data, use it; otherwise fallback to mock-based personalQuestionSets
    const mergedQuestionSets = fetchedQuestionSets.length > 0 ? fetchedQuestionSets : personalQuestionSets;

    const renderBookletModal = () => {
        if (!showBookletModal) return null;

        return (
            <div className="fixed inset-0 z-[250] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-10 animate-fade-in overflow-y-auto print:p-0 print:bg-white print:block">
                <div className="bg-white dark:bg-slate-900 w-full max-w-5xl rounded-[40px] shadow-2xl flex flex-col h-full max-h-[90vh] print:max-h-none print:shadow-none print:rounded-none print:w-full print:border-none print:block">
                    {/* Modal Header - Hidden on Print */}
                    <div className="px-10 py-8 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/5 print:hidden">
                        <div>
                            <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-4">
                                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-500/20 rounded-2xl flex items-center justify-center text-indigo-600">
                                    <DocumentTextIcon className="w-6 h-6" />
                                </div>
                                Kişisel Hata Kitapçığım
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">Yanlış yaptığın konulardan sana özel hazırlanan soru kitapçığı</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => window.print()}
                                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-sm hover:bg-indigo-700 transition-all shadow-lg active:scale-95"
                            >
                                <PrinterIcon className="w-5 h-5" />
                                Yazdır (PDF)
                            </button>
                            <button
                                onClick={() => setShowBookletModal(false)}
                                className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-800 border border-gray-100 dark:border-white/10 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm"
                                title="Kapat"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Booklet Content */}
                    <div className="flex-1 overflow-y-auto p-10 space-y-16 print:overflow-visible print:p-0 print:space-y-10">
                        {/* Booklet Cover Page (Print only) */}
                        <div className="hidden print:flex flex-col items-center justify-center h-[28cm] border-4 border-indigo-600 m-10 p-20 text-center">
                            <h1 className="text-6xl font-black text-indigo-900 mb-8">Supervisor Eğitim</h1>
                            <h2 className="text-4xl font-bold text-gray-800 mb-4">KİŞİSEL HATA KİTAPÇIĞI</h2>
                            <div className="w-40 h-2 bg-indigo-600 mb-12"></div>
                            <div className="space-y-4 text-2xl font-medium text-gray-600">
                                <p>Öğrenci: <span className="font-bold text-gray-900">{student.name}</span></p>
                                <p>Tarih: <span className="font-bold text-gray-900">{new Date().toLocaleDateString('tr-TR')}</span></p>
                                <p>Soru Sayısı: <span className="font-bold text-gray-900">{mergedQuestionSets.reduce((acc: number, s: any) => acc + s.count, 0)}</span></p>
                            </div>
                            <div className="mt-auto opacity-50 text-sm">Bu kitapçık yapay zeka tarafından senin eksiklerini kapatman için özel olarak hazırlandı.</div>
                        </div>

                        {mergedQuestionSets.map((set: any, setIdx: number) => (
                            <div key={setIdx} className="space-y-8 print:break-before-page">
                                <div className="border-b-2 border-indigo-600 pb-2 flex justify-between items-end">
                                    <h3 className="text-2xl font-black text-indigo-900 uppercase tracking-tighter">{set.subject} - {set.topic}</h3>
                                    <span className="text-sm font-bold text-gray-500 italic">Bölüm {setIdx + 1}</span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 print:grid-cols-2">
                                    {(set.questions || []).map((q: any, qIdx: number) => (
                                        <div key={qIdx} className="p-6 border border-gray-100 rounded-3xl dark:border-white/5 break-inside-avoid print:border-gray-200">
                                            <div className="flex gap-4 mb-4">
                                                <span className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-black text-sm shrink-0">
                                                    {qIdx + 1}
                                                </span>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-relaxed mb-6">
                                                        {q.text}
                                                    </p>

                                                    {q.image && (
                                                        <div className="mb-6 rounded-xl overflow-hidden border border-gray-100 dark:border-white/5">
                                                            <img src={q.image} alt="Soru" className="max-w-full h-auto mx-auto" />
                                                        </div>
                                                    )}

                                                    <div className="grid grid-cols-1 gap-2">
                                                        {(q.options || []).map((opt: string, optIdx: number) => (
                                                            <div key={optIdx} className="flex items-start gap-2 p-3 rounded-xl border border-gray-50 dark:border-white/5 bg-gray-50/30 dark:bg-white/5">
                                                                <span className="font-black text-xs text-indigo-600 mt-0.5">{String.fromCharCode(65 + optIdx)})</span>
                                                                <span className="text-[13px] text-gray-700 dark:text-gray-300 font-medium">{opt}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Answer Key Page (Print only) */}
                        <div className="hidden print:block break-before-page p-10">
                            <h3 className="text-3xl font-black text-indigo-900 border-b-4 border-indigo-600 pb-4 mb-10">CEVAP ANAHTARI</h3>
                            <div className="grid grid-cols-2 gap-x-20 gap-y-10">
                                {mergedQuestionSets.map((set: any, setIdx: number) => (
                                    <div key={setIdx} className="space-y-4">
                                        <h4 className="font-bold text-gray-900 border-b border-gray-200 pb-1">{set.subject} - {set.topic}</h4>
                                        <div className="flex flex-wrap gap-4">
                                            {(set.questions || []).map((q: any, qIdx: number) => (
                                                <div key={qIdx} className="flex items-center gap-2 tabular-nums">
                                                    <span className="font-bold text-gray-400">{qIdx + 1}.</span>
                                                    <span className="w-6 h-6 flex items-center justify-center bg-indigo-50 text-indigo-900 font-black rounded-md text-xs">
                                                        {String.fromCharCode(65 + (q.correct || 0))}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Print Styles */}
                {/* eslint-disable-next-line */}
                <style>{`
                    @media print {
                        @page { margin: 1.5cm; size: A4; }
                        body * { visibility: hidden; }
                        .fixed.inset-0.z-\\[250\\], .fixed.inset-0.z-\\[250\\] * { visibility: visible; }
                        .fixed.inset-0.z-\\[250\\] {
                            position: absolute;
                            inset: 0;
                            overflow: visible !important;
                            max-height: none !important;
                            height: auto !important;
                        }
                        .fixed.inset-0.z-\\[250\\] > div {
                            max-height: none !important;
                            height: auto !important;
                            overflow: visible !important;
                            border-radius: 0 !important;
                            box-shadow: none !important;
                        }
                        .fixed.inset-0.z-\\[250\\] .overflow-y-auto {
                            overflow: visible !important;
                            max-height: none !important;
                        }
                        .fixed.inset-0.z-\\[250\\] [class*="rounded-3xl"] {
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }
                    }
                `}</style>
            </div>
        );
    };

    const renderMistakeReport = () => {
        if (!lastExam) return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <ClipboardListIcon className="w-16 h-16 mb-4 opacity-20" />
                <p>Henüz analiz edilecek bir deneme sınavı bulunmuyor.</p>
            </div>
        );

        // Group questions or topic analysis by subject
        const analysisBySubject: Record<string, any[]> = {};
        const hasDetailedQuestions = lastExam.questions && lastExam.questions.length > 0;

        if (hasDetailedQuestions) {
            lastExam.questions!.forEach((q: any) => {
                const subj = q.subject === 'Temel Matematik' ? 'Matematik' : q.subject;
                if (!analysisBySubject[subj]) analysisBySubject[subj] = [];
                analysisBySubject[subj].push(q);
            });
        } else if (lastExam.topicAnalysis && lastExam.topicAnalysis.length > 0) {
            lastExam.topicAnalysis.forEach((t: any) => {
                if (!analysisBySubject[t.subject]) analysisBySubject[t.subject] = [];
                analysisBySubject[t.subject].push(t);
            });
        }
        
        // Filter out elective subjects for TYT to maintain 120 total questions
        if (subTab === 'TYT') {
            delete analysisBySubject['Seç. Felsefe'];
            delete analysisBySubject['Seçmeli Felsefe'];
        }

        const successRate = Math.round((lastExam.totalCorrect / (lastExam.totalCorrect + lastExam.totalIncorrect + lastExam.totalEmpty)) * 100) || 0;

        // Filtered exams for search
        const filteredExams = student.trialExams
            .filter(e => e.examType === subTab)
            .filter(e => (e.name || "").toLowerCase().includes((examSearchQuery || "").toLowerCase()))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        return (
            <div className="space-y-8 animate-fade-in">
                {/* Search and Selection Header */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-white/10 shadow-sm relative z-[100]">
                    <div className="relative w-full">
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Geçmiş denemelerde ara..."
                            value={examSearchQuery}
                            onChange={(e) => {
                                setExamSearchQuery(e.target.value);
                                setShowSearchDropdown(true);
                            }}
                            onFocus={() => setShowSearchDropdown(true)}
                            onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                            className="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                        />
                        {/* Dropdown Results */}
                        <div className={`absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-gray-100 dark:border-white/10 rounded-[28px] shadow-2xl overflow-hidden transition-all z-[110] ${(showSearchDropdown && filteredExams.length > 0) ? 'max-h-[400px] py-2 opacity-100 visible' : 'max-h-0 opacity-0 invisible pointer-events-none'
                            }`}>
                            <div className="px-6 py-3 border-b border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5 flex justify-between items-center">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Kayıtlı Denemeler</span>
                                <span className="text-[10px] font-black text-blue-500 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full">{filteredExams.length} Deneme</span>
                            </div>
                            <div className="overflow-y-auto max-h-[320px] custom-scrollbar">
                                {filteredExams.map((e) => (
                                    <button
                                        key={e.id}
                                        onClick={() => {
                                            setSelectedExamId(e.id);
                                            setShowSearchDropdown(false);
                                            setExamSearchQuery("");
                                        }}
                                        className={`w-full px-8 py-5 text-left flex items-center justify-between transition-all hover:bg-blue-50 dark:hover:bg-blue-600/10 group ${lastExam?.id === e.id ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''
                                            }`}
                                    >
                                        <div className="flex flex-col">
                                            <span className={`text-base font-bold transition-colors ${lastExam?.id === e.id ? 'text-blue-600' : 'text-gray-900 dark:text-white'}`}>
                                                {e.name}
                                            </span>
                                            <div className="flex items-center gap-3 mt-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                                <span>{new Date(e.date).toLocaleDateString('tr-TR')}</span>
                                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                                <span className="text-blue-500/70">{e.totalNet.toFixed(2)} Net</span>
                                            </div>
                                        </div>
                                        {lastExam?.id === e.id ? (
                                            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
                                                <CheckCircleIcon className="w-5 h-5" />
                                            </div>
                                        ) : (
                                            <ArrowRightIcon className="w-5 h-5 text-gray-300 group-hover:text-blue-400 transition-all group-hover:translate-x-1" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dashboard Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Overall Success Circular Card */}
                    <div className="bg-white dark:bg-slate-900 rounded-[32px] p-8 border border-gray-100 dark:border-white/10 shadow-sm flex flex-col items-center text-center">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Genel Başarı</h3>
                        <div className="relative w-48 h-48 flex items-center justify-center">
                            <svg className="w-full h-full -rotate-90">
                                <circle cx="96" cy="96" r="80" fill="none" stroke="currentColor" strokeWidth="16" className="text-gray-100 dark:text-gray-800" />
                                <circle cx="96" cy="96" r="80" fill="none" stroke="currentColor" strokeWidth="16" strokeDasharray={502.4} strokeDashoffset={502.4 * (1 - successRate / 100)} className="text-blue-600 dark:text-blue-400 transition-all duration-1000" strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-5xl font-black text-gray-900 dark:text-white">%{successRate}</span>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Genel Oran</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 w-full mt-8">
                            <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-2xl">
                                <p className="text-[10px] font-bold text-gray-400 uppercase">Sıralama</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                    {ranking && ranking.rank > 0 ? `${ranking.rank} / ${ranking.total}` : '- / -'}
                                </p>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded-2xl">
                                <p className="text-[10px] font-bold text-blue-400 dark:text-blue-500 uppercase">Puan</p>
                                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{kpiData.rawScore}</p>
                            </div>
                        </div>
                    </div>

                    {/* Middle Section: Scores & Performance */}
                    <div className="lg:col-span-3 space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-orange-100 dark:border-orange-500/20 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                                <div className="flex justify-between items-start relative z-10">
                                    <div>
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Sınav Puanım</p>
                                        <h4 className="text-3xl font-black text-orange-600">{kpiData.rawScore}</h4>
                                    </div>
                                    <div className="w-10 h-10 bg-orange-100 dark:bg-orange-500/20 rounded-xl flex items-center justify-center text-orange-600 drop-shadow-sm">
                                        <AwardIcon className="w-6 h-6" />
                                    </div>
                                </div>
                                <div className="flex gap-4 mt-6">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-bold text-green-500 uppercase flex items-center gap-1"><CheckCircleIcon className="w-3 h-3" /> Doğru</span>
                                        <span className="text-lg font-bold text-gray-900 dark:text-white">{lastExam.totalCorrect}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-bold text-red-500 uppercase flex items-center gap-1"><XCircleIcon className="w-3 h-3" /> Yanlış</span>
                                        <span className="text-lg font-bold text-gray-900 dark:text-white">{lastExam.totalIncorrect}</span>
                                    </div>
                                    <div className="flex flex-col ml-auto">
                                        <span className="text-[10px] font-bold text-blue-500 uppercase">Σ Net</span>
                                        <span className="text-lg font-bold text-gray-900 dark:text-white">{lastExam.totalNet.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-blue-100 dark:border-blue-500/20 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                                <div className="flex justify-between items-start relative z-10">
                                    <div>
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Genel Puan Ort.</p>
                                        <h4 className="text-3xl font-black text-blue-600">{kpiData.rawScore}</h4>
                                    </div>
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-600 drop-shadow-sm">
                                        <ChartBarIcon className="w-6 h-6" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Lesson Based Performance Indicator - Updated to BarChart */}
                        <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-gray-100 dark:border-white/10 shadow-sm">
                            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                                Ders Bazlı Performans (%)
                            </h4>
                            <div className="h-[250px] w-full mt-4">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={Object.entries(lastExam.scores).map(([subj, data]: [string, any]) => ({
                                        name: subj.substring(0, 8),
                                        fullName: subj,
                                        basari: Math.round((data.correct / (data.correct + data.incorrect + data.empty)) * 100) || 0,
                                        net: data.net,
                                        dogru: data.correct,
                                        yanlis: data.incorrect,
                                        bos: data.empty
                                    }))}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.1} />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }} />
                                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                        <RechartsTooltip
                                            contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                            formatter={(value, name) => [value, name === 'basari' ? 'Başarı %' : String(name || "").toUpperCase()]}
                                        />
                                        <Line type="monotone" dataKey="basari" stroke="#4148ed" strokeWidth={3} dot={{ r: 4, fill: '#4148ed' }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-8">
                                {Object.entries(lastExam.scores).map(([subj, data]: [string, any], idx) => {
                                    const total = data.correct + data.incorrect + data.empty;
                                    const subjRate = total > 0 ? Math.round((data.correct / total) * 100) : 0;
                                    const colors = ['#4148ed', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#f43f5e', '#fbbf24', '#2dd4bf'];
                                    const cardColor = colors[idx % colors.length];

                                    return (
                                        <div key={subj} className="bg-white dark:bg-white/5 p-4 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm flex items-center justify-between group hover:shadow-md transition-all">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cardColor }}></div>
                                                    <span className="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-wider truncate">{subj}</span>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-tighter mb-0.5">D</span>
                                                        <span className="text-xs font-bold text-emerald-500">{data.correct}</span>
                                                    </div>
                                                    <div className="w-px h-6 bg-gray-100 dark:bg-white/5"></div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-tighter mb-0.5">Y</span>
                                                        <span className="text-xs font-bold text-rose-500">{data.incorrect}</span>
                                                    </div>
                                                    <div className="w-px h-6 bg-gray-100 dark:bg-white/5"></div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-tighter mb-0.5">B</span>
                                                        <span className="text-xs font-bold text-gray-400">{data.empty}</span>
                                                    </div>
                                                    <div className="w-px h-6 bg-gray-100 dark:bg-white/5"></div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-tighter mb-0.5">NET</span>
                                                        <span className="text-xs font-black text-blue-600 dark:text-blue-400">{data.net.toFixed(2)}N</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="w-16 h-16 relative shrink-0 ml-4">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={[
                                                                { name: 'Başarı', value: subjRate },
                                                                { name: 'Kalan', value: 100 - subjRate }
                                                            ]}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={22}
                                                            outerRadius={28}
                                                            startAngle={90}
                                                            endAngle={-270}
                                                            dataKey="value"
                                                            stroke="none"
                                                        >
                                                            <Cell fill={cardColor} />
                                                            <Cell fill="rgba(0,0,0,0.05)" />
                                                        </Pie>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-[10px] font-black text-gray-900 dark:text-white">%{subjRate}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>



                {/* Kazanım Analizi Grouped By Subjects */}
                <h3 className="text-2xl font-black text-gray-900 dark:text-white mt-12 mb-6 tracking-tight">Kazanım Analizi</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {Object.entries(analysisBySubject).map(([subject, items]) => {
                        const subjStats = lastExam.scores[subject] || { correct: 0, incorrect: 0, empty: 0, net: 0 };
                        const colorMap: any = {
                            'Türkçe': 'bg-blue-600',
                            'Matematik': 'bg-rose-500',
                            'Fen Bilimleri': 'bg-emerald-500',
                            'Sosyal Bilimler': 'bg-amber-500',
                            'İngilizce': 'bg-purple-600',
                            'Fizik': 'bg-cyan-600',
                            'Kimya': 'bg-teal-500',
                            'Biyoloji': 'bg-lime-600',
                            'Tarih': 'bg-orange-700',
                            'Coğrafya': 'bg-sky-700',
                            'Felsefe': 'bg-indigo-700',
                            'Din Kültürü': 'bg-amber-600',
                            'T.C. İnkılap Tarihi': 'bg-orange-600',
                            'Yabancı Dil': 'bg-purple-600'
                        };
                        const bgColor = colorMap[subject] || 'bg-indigo-600';
                        const icons: any = {
                            'Türkçe': <BookOpenIcon className="w-5 h-5" />,
                            'Matematik': <CalculatorIcon className="w-5 h-5" />,
                            'Fen Bilimleri': <LightBulbIcon className="w-5 h-5" />,
                            'Sosyal Bilimler': <GlobeIcon className="w-5 h-5" />,
                            'İngilizce': <GlobeIcon className="w-5 h-5" />,
                            'Fizik': <FireIcon className="w-5 h-5" />,
                            'Kimya': <AcademicCapIcon className="w-5 h-5" />,
                            'Biyoloji': <TrendingUpIcon className="w-5 h-5" />,
                            'Tarih': <BookmarkIcon className="w-5 h-5" />,
                            'T.C. İnkılap Tarihi': <BookmarkIcon className="w-5 h-5" />,
                            'Coğrafya': <GlobeIcon className="w-5 h-5" />,
                            'Felsefe': <LightBulbIcon className="w-5 h-5" />,
                            'Din Kültürü': <AwardIcon className="w-5 h-5" />,
                            'Yabancı Dil': <GlobeIcon className="w-5 h-5" />
                        };

                        return (
                            <div key={subject} className="bg-white dark:bg-slate-900 rounded-[30px] border border-gray-100 dark:border-white/10 shadow-sm overflow-hidden flex flex-col">
                                <div className={`${bgColor} p-6 text-white`}>
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-xl font-black tracking-tight">{subject.toUpperCase()}</h4>
                                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                                            {icons[subject] || <AcademicCapIcon className="w-5 h-5" />}
                                        </div>
                                    </div>
                                    <div className="flex gap-4 text-[11px] font-black text-white/80">
                                        <span>D: {subjStats.correct}</span>
                                        <span>Y: {subjStats.incorrect}</span>
                                        <span>B: {subjStats.empty}</span>
                                        <span className="ml-auto">NET: {subjStats.net.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="p-2 flex-1">
                                    <table className="w-full text-left">
                                        <thead className="text-[10px] font-black text-gray-400 uppercase">
                                            <tr>
                                                <th className="px-4 py-3">Soru</th>
                                                <th className="px-4 py-3">Kazanım</th>
                                                <th className="px-4 py-3 text-right">Durum</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y dark:divide-white/5">
                                            {items.map((item, idx) => {
                                                if (hasDetailedQuestions) {
                                                    const q = item;
                                                    const studentAns = lastExam.studentAnswers ? lastExam.studentAnswers[q.id] : null;
                                                    const isCorrect = studentAns === q.correctOption;
                                                    const isEmpty = !studentAns;

                                                    return (
                                                        <tr key={q.id} className="group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                                            <td className="px-4 py-3 text-sm font-bold text-gray-500">{q.questionNumber}</td>
                                                            <td className="px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 whitespace-normal leading-relaxed">{q.topic || 'Genel Konu'}</td>
                                                            <td className="px-4 py-3 text-right">
                                                                {isCorrect ? (
                                                                    <span className="px-2 py-0.5 bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 rounded-md text-[10px] font-black">D</span>
                                                                ) : isEmpty ? (
                                                                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 text-gray-400 rounded-md text-[10px] font-black">B</span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded-md text-[10px] font-black">Y</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                } else {
                                                    const t = item;
                                                    return (
                                                        <tr key={idx} className="group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                                            <td className="px-4 py-3 text-sm font-bold text-gray-500">-</td>
                                                            <td className="px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 whitespace-normal leading-relaxed">{t.topic}</td>
                                                            <td className="px-4 py-3 text-right space-x-1">
                                                                {t.correct > 0 && <span className="text-green-600 font-bold">{t.correct}D</span>}
                                                                {t.incorrect > 0 && <span className="text-red-600 font-bold">{t.incorrect}Y</span>}
                                                                {t.empty > 0 && <span className="text-gray-400 font-bold">{t.empty}B</span>}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            })}
                                        </tbody>
                                    </table>
                                    {items.length > 5 && (
                                        <button className="w-full py-3 mt-2 text-[11px] font-black text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-xl transition-colors tracking-widest uppercase">
                                            TÜMÜNÜ GÖR
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Soru Dağılımı - Pie Chart View (Full Width / Horizontal) - Relocated to Bottom */}
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-[0_15px_40px_rgba(0,0,0,0.04)] border border-gray-100 dark:border-white/5 flex flex-col mt-12">
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="text-base font-black text-gray-900 dark:text-white uppercase tracking-widest flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                            Soru Dağılımı Analizi
                        </h4>
                        <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-[11px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-tighter">
                            <ChartBarIcon className="w-4 h-4" />
                            Ders ve Konu Bazlı Dağılım
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row items-center gap-12">
                        {Object.entries(analysisBySubject).length > 0 ? (
                            <>
                                <div className="w-full lg:w-[400px] h-[300px] relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={Object.entries(analysisBySubject).map(([subj, items]) => ({
                                                    name: subj,
                                                    value: items.reduce((acc, t) => acc + (t.questions?.length || (t.incorrect + t.empty + t.correct) || 1), 0)
                                                }))}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={80}
                                                outerRadius={110}
                                                paddingAngle={8}
                                                dataKey="value"
                                            >
                                                {Object.keys(analysisBySubject).map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={['#4148ed', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#f43f5e', '#fbbf24', '#2dd4bf'][index % 8]} stroke="none" />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip
                                                contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)', padding: '16px' }}
                                                itemStyle={{ fontWeight: 800, fontSize: '12px' }}
                                                formatter={(value) => [`${value} Soru`, 'Sayı']}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                        <span className="text-4xl font-black text-gray-900 dark:text-white">
                                            {Object.values(analysisBySubject).reduce((acc, items) => acc + items.reduce((tAcc: number, t: any) => tAcc + (t.questions?.length || (t.incorrect + t.empty + t.correct) || 0), 0), 0)}
                                        </span>
                                        <span className="text-xs font-black text-gray-400 uppercase tracking-widest mt-1">Toplam Soru</span>
                                    </div>
                                </div>

                                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 w-full max-h-[340px] overflow-y-auto pr-6 custom-scrollbar">
                                    {Object.entries(analysisBySubject).map(([subj, items], idx) => {
                                        const subjTotal = items.reduce((acc, t) => acc + (t.questions?.length || (t.incorrect + t.empty + t.correct) || 0), 0);
                                        const colors = ['bg-blue-600', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-rose-500', 'bg-yellow-500', 'bg-teal-500'];
                                        return (
                                            <div key={subj} className="space-y-2 p-4 rounded-3xl bg-gray-50/50 dark:bg-white/5 border border-transparent hover:border-blue-100 dark:hover:border-blue-900/30 transition-all group">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-3 h-3 rounded-full ${colors[idx % 8]} shadow-lg shadow-${colors[idx % 8].split('-')[1]}-500/20`}></div>
                                                        <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">{subj}</span>
                                                    </div>
                                                    <span className="text-xs font-black text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-lg">{subjTotal} Soru</span>
                                                </div>
                                                <div className="pl-6 space-y-2">
                                                    {items.slice(0, 4).map((t, tIdx) => (
                                                        <div key={tIdx} className="flex justify-between items-start text-[11px] text-gray-500 dark:text-gray-400 font-bold group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors py-0.5">
                                                            <div className="flex items-start gap-2 flex-1">
                                                                <div className="w-1 h-1 bg-gray-300 rounded-full mt-1.5 shrink-0"></div>
                                                                <span className="whitespace-normal leading-relaxed">{t.topic || "Genel"}</span>
                                                            </div>
                                                            <span className="ml-4 tabular-nums shrink-0">{t.questions?.length || (t.incorrect + t.empty + t.correct)}</span>
                                                        </div>
                                                    ))}
                                                    {items.length > 4 && (
                                                        <div className="pt-1 flex items-center gap-2 text-[10px] text-blue-400 font-black uppercase tracking-tighter italic">
                                                            <PlusCircleIcon className="w-3 h-3" />
                                                            {items.length - 4} Konu Daha Listeleniyor
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="py-24 text-center w-full flex flex-col items-center">
                                <div className="w-20 h-20 bg-gray-50 dark:bg-white/5 rounded-[32px] flex items-center justify-center mb-6 shadow-inner">
                                    <SearchIcon className="w-10 h-10 text-gray-200 dark:text-gray-700" />
                                </div>
                                <p className="text-gray-400 font-extrabold text-base tracking-tight">Analiz verisi henüz işlenmedi.</p>
                                <p className="text-gray-300 text-xs mt-2 uppercase tracking-widest">Deneme detaylarını girerek başlayın</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Advisor Note */}
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-gray-100 dark:border-white/10 shadow-sm flex flex-col md:flex-row items-center gap-8 mt-12">
                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-500/20 rounded-2xl flex items-center justify-center text-blue-600 shrink-0">
                        <ChatIcon className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                        <h4 className="text-lg font-black text-gray-900 dark:text-white mb-1">Danışman Notu</h4>
                        <p className="text-gray-500 dark:text-gray-400 italic">"Performansın genel ortalamaya göre gayet iyi durumda. Matematik netlerin yükselişte, özellikle problem çözme hızını korumalısın. Fen bilimlerinde Kimya konularını tekrar etmemiz gerekiyor."</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setShowBookletModal(true)}
                            className="px-8 py-3.5 bg-blue-600 text-white font-black rounded-2xl shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-all active:scale-95 text-sm"
                        >
                            Analiz PDF İndir
                        </button>
                        <button className="px-8 py-3.5 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white font-black rounded-2xl border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10 transition-all text-sm">Paylaş</button>
                    </div>
                </div>
            </div >
        );
    };

    return (
        <div className="space-y-8 animate-fade-in pb-24">
            {/* Header & Controls */}
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8 mt-2">
                <div className="flex-1">
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight max-w-2xl leading-snug">
                        Netlerini gir, gelişimini takip et ve eksiklerini keşfet.
                    </h2>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {/* Exam Type Tabs */}
                    <div className="flex bg-gray-200/50 dark:bg-black/20 p-1.5 rounded-xl shadow-inner border border-gray-300/30 dark:border-white/5 backdrop-blur-md">
                        {['TYT', 'AYT', 'YDT', 'LGS'].map(t => (
                            <button
                                key={t}
                                onClick={() => handleTabChange(t as any)}
                                className={`px-5 py-2 rounded-lg text-xs font-bold transition-all duration-300 ${subTab === t
                                    ? 'bg-white shadow-sm text-blue-600 scale-105'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-white/10'}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 p-1.5 bg-white/40 dark:bg-slate-900/20 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-white/10 shadow-sm">
                        {/* Hata Karnesi Button */}
                        <button
                            onClick={() => setMainTab(mainTab === 'MISTAKE_REPORT' ? 'TRACKING' : 'MISTAKE_REPORT')}
                            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition-all duration-300 ${mainTab === 'MISTAKE_REPORT'
                                ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-md'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600'
                                }`}
                        >
                            <ExclamationIcon className="w-3.5 h-3.5" />
                            Hata Karnesi
                        </button>

                        {/* Akıllı Analiz Button */}
                        <button
                            onClick={() => setMainTab(mainTab === 'ANALYSIS' ? 'TRACKING' : 'ANALYSIS')}
                            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition-all duration-300 ${mainTab === 'ANALYSIS'
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:text-purple-600'
                                }`}
                        >
                            <LightBulbIcon className="w-3.5 h-3.5" />
                            Akıllı Analiz
                        </button>

                        {/* Kişisel Soru Bankam Button */}
                        <button
                            onClick={() => setMainTab(mainTab === 'QUESTION_BANK' ? 'TRACKING' : 'QUESTION_BANK')}
                            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition-all duration-300 ${mainTab === 'QUESTION_BANK'
                                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:text-indigo-600'
                                }`}
                        >
                            <BookOpenIcon className="w-3.5 h-3.5" />
                            Soru Bankam
                        </button>
                    </div>

                    <button
                        onClick={() => openAddModal()}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xs transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20 active:scale-95 uppercase tracking-widest"
                    >
                        <PlusCircleIcon className="w-4 h-4" /> Deneme Ekle
                    </button>
                </div>
            </div>

            {mainTab === 'TRACKING' ? (
                <div className="space-y-8">
                    {/* Row 1: Last Exam Summary & Development Chart */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Son Deneme Karnesi Card with Flip Animation */}
                        <div className="lg:col-span-5 h-[480px] perspective-1000 group">
                            <div className={`relative w-full h-full transition-all duration-700 preserve-3d cursor-pointer ${isFlipped ? 'rotate-y-180' : ''}`}>

                                {/* FRONT FACE: Detail view of specific exam */}
                                <div className={`absolute inset-0 bg-gradient-to-br from-[#4148ed] to-[#252acb] p-10 rounded-[40px] shadow-[0_20px_50px_rgba(43,43,238,0.3)] text-white overflow-hidden backface-hidden transition-all duration-300 ${isFlipped ? 'pointer-events-none z-0 opacity-0' : 'pointer-events-auto z-10 opacity-100'}`}>
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" />
                                    <div className="relative z-10 flex flex-col h-full">
                                        <div className="flex items-center justify-between mb-10">
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-inner">
                                                    <ChartBarIcon className="w-8 h-8 text-white" />
                                                </div>
                                                <h3 className="font-black text-2xl tracking-tight">Deneme Karnesi</h3>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setIsFlipped(true); }}
                                                className="relative z-20 px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-widest border border-white/20 hover:bg-white/30 transition-colors"
                                            >
                                                Tümü / Çevir
                                            </button>
                                        </div>

                                        {lastExam ? (
                                            <div className="flex flex-col flex-1 justify-between">
                                                <div className="space-y-2">
                                                    <p className="text-white/60 text-sm font-bold uppercase tracking-widest">{lastExam.name}</p>
                                                    <div className="flex items-end justify-between">
                                                        <div className="flex flex-col">
                                                            <span className="text-white/40 text-xs font-bold uppercase mb-1">Toplam Net</span>
                                                            <span className="text-7xl font-black tracking-tighter tabular-nums drop-shadow-md">{lastExam.totalNet}</span>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-white/40 text-[10px] font-bold uppercase mb-1 block">Başarı Sırası</span>
                                                            <span className="text-3xl font-black tabular-nums tracking-tight">124 <span className="text-sm font-bold text-white/50">/ 45k</span></span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-4 gap-3">
                                                    {(subTab === 'LGS' ? [
                                                        { label: 'TÜRKÇE', key: 'Türkçe' },
                                                        { label: 'MATEMATİK', key: 'Matematik' },
                                                        { label: 'FEN BİL.', key: 'Fen Bilimleri' },
                                                        { label: 'İNKILAP', key: 'T.C. İnkılap Tarihi' },
                                                    ] : subTab === 'AYT' ? [
                                                        { label: 'MATEMATİK', key: 'Matematik' },
                                                        { label: 'EDEBİYAT', key: 'Edebiyat' },
                                                        { label: 'FİZİK', key: 'Fizik' },
                                                        { label: 'KİMYA', key: 'Kimya' },
                                                    ] : [
                                                        { label: 'TÜRKÇE', key: 'Türkçe' },
                                                        { label: 'MATEMATİK', key: 'Matematik' },
                                                        { label: 'FEN BİL.', key: 'Fen Bilimleri' },
                                                        { label: 'SOSYAL', key: 'Sosyal Bilimler' },
                                                    ]).map((sub, i) => {
                                                        const score = lastExam.scores[sub.key] || { correct: 0, incorrect: 0, empty: 0, net: 0 };
                                                        return (
                                                            <div key={i} className="bg-white/10 backdrop-blur-md rounded-[28px] p-4 text-center border border-white/10 hover:bg-white/20 transition-all">
                                                                <p className="text-[9px] font-black tracking-widest text-white/50 mb-2 truncate">{sub.label}</p>
                                                                <div className="flex flex-col gap-0.5">
                                                                    <p className="text-lg font-black leading-none">{score.net}</p>
                                                                    <p className="text-[8px] font-bold text-white/40 leading-none mt-1">
                                                                        {score.correct}D {score.incorrect}Y {score.empty}B
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex-1 flex flex-col items-center justify-center text-white/40">
                                                <div className="text-4xl mb-4">📉</div>
                                                <p className="font-bold">Henüz {subTab} denemesi girilmedi.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* BACK FACE: List of all exams */}
                                <div className={`absolute inset-0 bg-white dark:bg-slate-900 p-10 rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-100 dark:border-white/5 overflow-hidden rotate-y-180 backface-hidden transition-all duration-300 ${!isFlipped ? 'pointer-events-none z-0 opacity-0' : 'pointer-events-auto z-10 opacity-100'}`}>
                                    <div className="relative z-10 flex flex-col h-full text-gray-900 dark:text-white">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="font-black text-2xl tracking-tight">Geçmiş Denemeler</h3>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsFlipped(false);
                                                }}
                                                title="Listeyi Kapat"
                                                className="relative z-20 w-10 h-10 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                                            >
                                                <XCircleIcon className="w-6 h-6 text-gray-400" />
                                            </button>
                                        </div>

                                        <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
                                            {sortedExams.map((e, idx) => (
                                                <div
                                                    key={e.id}
                                                    onClick={(ev) => {
                                                        ev.stopPropagation();
                                                        setSelectedExamId(e.id);
                                                        setIsFlipped(false);
                                                    }}
                                                    className={`p-4 rounded-3xl border transition-all flex items-center justify-between group ${lastExam?.id === e.id
                                                        ? 'bg-blue-50 border-blue-100 dark:bg-blue-500/10 dark:border-blue-500/20'
                                                        : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-white/5 hover:border-blue-200'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs ${lastExam?.id === e.id ? 'bg-blue-500 text-white' : 'bg-white dark:bg-slate-800 text-gray-400'
                                                            }`}>
                                                            {sortedExams.length - idx}
                                                        </div>
                                                        <div>
                                                            <p className={`font-black text-sm ${lastExam?.id === e.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>{e.name || "Adsız Deneme"}</p>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{new Date(e.date).toLocaleDateString('tr-TR')}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-black tracking-tighter">{e.totalNet} <span className="text-[10px] text-gray-400">Net</span></p>
                                                    </div>
                                                </div>
                                            ))}
                                            {sortedExams.length === 0 && (
                                                <p className="text-center py-20 text-gray-400 italic">Henüz deneme kaydı yok.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Net Gelişimi Area Chart */}
                        <div className="lg:col-span-7 bg-white p-10 rounded-[40px] shadow-[0_15px_40px_rgba(0,0,0,0.04)] border border-gray-100 flex flex-col relative group">
                            <div className="flex justify-between items-center mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                                        <TrendingUpIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 leading-none">Net Gelişimi ({subTab})</h3>
                                        <p className="text-gray-400 text-sm mt-1">Son 6 aylık performansın</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button title="Daha fazla seçenek" className="p-2 hover:bg-gray-50 rounded-lg text-gray-400 transition-colors"><DotsVerticalIcon className="w-5 h-5" /></button>
                                </div>
                            </div>

                            <div className="flex-1 min-h-[300px]">
                                {chartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#4148ed" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#4148ed" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 600 }} dy={10} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 600 }} />
                                            <RechartsTooltip
                                                contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', padding: '12px 16px' }}
                                                itemStyle={{ fontWeight: 800, color: '#4148ed' }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="net"
                                                stroke="#4148ed"
                                                strokeWidth={4}
                                                fillOpacity={1}
                                                fill="url(#colorNet)"
                                                dot={{ r: 6, fill: '#4148ed', strokeWidth: 3, stroke: '#fff' }}
                                                activeDot={{ r: 8, fill: '#fff', stroke: '#4148ed', strokeWidth: 4 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-gray-300">
                                        <ChartBarIcon className="w-20 h-20 opacity-10 mb-4" />
                                        <p className="font-bold">Analiz için yeterli veri yok.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Kritik Konu Analizi & Son Denemeler/Ortalamalar Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        {/* Kritik Konu Analizi */}
                        <div className="lg:col-span-5 bg-white p-10 rounded-[40px] shadow-[0_15px_40px_rgba(0,0,0,0.04)] border border-gray-100 h-full">
                            <div className="flex items-center gap-4 mb-10">
                                <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center">
                                    <ExclamationIcon className="w-6 h-6" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 tracking-tight">Kritik Konu Analizi</h3>
                            </div>

                            {patterns.length > 0 ? (
                                <div className="space-y-6">
                                    {patterns.map(([key, stat], idx) => {
                                        const percentage = 100 - Math.round((stat.total / (relevantExams.length * 5)) * 100); // Örnek hesaplama
                                        const colorClass = percentage > 80 ? 'text-emerald-500 bg-emerald-50' : percentage > 60 ? 'text-orange-500 bg-orange-50' : 'text-rose-500 bg-rose-50';
                                        return (
                                            <div key={key} className="flex items-center gap-6 p-6 bg-gray-50/50 rounded-3xl border border-gray-100 hover:bg-gray-100 transition-all cursor-pointer group shadow-sm">
                                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center font-black text-lg shadow-sm border border-white ${colorClass}`}>
                                                    {percentage}%
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="font-black text-gray-900 text-lg leading-tight group-hover:text-blue-600 transition-colors">{key.split(' - ')[1]}</h4>
                                                    <p className="text-gray-400 text-sm mt-1 font-bold">Son 3 denemede {stat.total} yanlış/boş</p>
                                                </div>
                                                <ChevronRightIcon className="w-6 h-6 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                                            </div>
                                        );
                                    })}
                                    <button 
                                        onClick={() => setMainTab('ANALYSIS')}
                                        className="w-full py-5 rounded-[30px] border-2 border-dashed border-gray-200 text-gray-400 font-black text-sm hover:bg-gray-50 hover:border-blue-500 hover:text-blue-500 transition-all shadow-sm"
                                    >
                                        Tüm Analizi Gör
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center py-20 text-gray-300 italic font-medium bg-gray-50/50 rounded-[40px] border-2 border-dashed border-gray-100">
                                    Analiz için {subTab} türünden en az 3 deneme gerekli. Şu an: {relevantExams.length}/3
                                </div>
                            )}
                        </div>

                        {/* Right Content Group: Son Denemeler & Ders Ortalamaları */}
                        <div className="lg:col-span-7 flex flex-col gap-8 h-full">
                            {/* Son Denemeler (Half Row) */}
                            <div className="bg-white p-10 rounded-[40px] shadow-[0_15px_40px_rgba(0,0,0,0.04)] border border-gray-100 flex-1">
                                <div className="flex justify-between items-center mb-8">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-slate-50 text-slate-600 rounded-2xl flex items-center justify-center">
                                            <BookmarkIcon className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-2xl font-black text-gray-900 tracking-tight">Son Denemeler</h3>
                                    </div>
                                    <button className="text-blue-600 font-black text-sm hover:underline tracking-tight">Tümü</button>
                                </div>
                                <div className="space-y-4">
                                    {sortedRelevantExams.slice(0, 3).map((e, i) => (
                                        <div
                                            key={i}
                                            onClick={() => {
                                                setSelectedExamId(e.id);
                                                setMainTab('MISTAKE_REPORT');
                                            }}
                                            className="flex items-center gap-5 p-5 bg-gray-50 rounded-3xl border border-gray-100 group hover:border-blue-200 transition-all cursor-pointer"
                                        >
                                            <div className="w-14 h-14 bg-gradient-to-br from-slate-200 to-slate-400 rounded-2xl flex items-center justify-center text-white shadow-sm font-black overflow-hidden relative">
                                                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                                                <GlobeIcon className="w-8 h-8 relative z-10" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-black text-gray-900 truncate max-w-[180px]">{e.name}</h4>
                                                <p className="text-gray-400 text-xs font-bold mt-1 uppercase tracking-widest">{new Date(e.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })} • {e.totalNet} Net</p>
                                            </div>
                                            <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-300 group-hover:text-blue-600 group-hover:border-blue-100 transition-all">
                                                <ArrowRightIcon className="w-5 h-5" />
                                            </div>
                                        </div>
                                    ))}
                                    {sortedRelevantExams.length === 0 && (
                                        <div className="text-center py-10 text-gray-400 italic">Henüz deneme kaydı yok.</div>
                                    )}
                                </div>
                            </div>

                            {/* Ders Ortalamaları (Half Row) */}
                            <div className="bg-white p-10 rounded-[40px] shadow-[0_15px_40px_rgba(0,0,0,0.04)] border border-gray-100 flex-1">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center">
                                        <TrendingUpIcon className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 tracking-tight">Ders Ortalamaları</h3>
                                </div>
                                <div className="space-y-5">
                                    {(subjectAverages || []).slice(0, 12).map((item, i) => (
                                        <div key={i} className="space-y-2">
                                            <div className="flex justify-between items-center text-sm font-black">
                                                <span className="text-gray-700">{item.subject}</span>
                                                <span className="text-gray-900 tracking-tight">{item.average}</span>
                                            </div>
                                            <div
                                                className="h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-50 shadow-inner"
                                                {...{ style: { '--avg-w': `${(parseFloat(item.average) / (subTab === 'TYT' ? 40 : 40)) * 100}%` } as React.CSSProperties }}
                                                title={`${item.subject} Ortalama: ${item.average}`}
                                            >
                                                <div
                                                    className="h-full bg-blue-500 rounded-full transition-all duration-1000 shadow-[2px_0_5px_rgba(0,0,0,0.1)] w-[var(--avg-w)]"
                                                    {...{ style: { '--avg-w': `${(parseFloat(item.average) / (subTab === 'TYT' ? 40 : 40)) * 100}%` } as React.CSSProperties }}
                                                    title={`${item.subject} Ortalama: ${item.average}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    {(!subjectAverages || subjectAverages.length === 0) && (
                                        <div className="text-center py-10 text-gray-400 italic">
                                            Ortalamalar için en az 3 deneme gerekli.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Bottom Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { label: 'TOPLAM DENEME', val: student.trialExams.length, icon: '📊', color: 'text-blue-500 bg-blue-50' },
                            { label: 'ORTALAMA NET', val: (chartData.length > 0 ? (chartData.reduce((a, b) => a + b.net, 0) / chartData.length).toFixed(2) : '0'), icon: '📈', color: 'text-emerald-500 bg-emerald-50' },
                            { label: 'SORU HIZI', val: kpiData.speed, icon: '⏱️', color: 'text-amber-500 bg-amber-50', sub: 'Soru Başına' },
                            { label: 'BAŞARI PUANI', val: kpiData.placementScore, icon: '🚀', color: 'text-purple-500 bg-purple-50', sub: `Ham: ${kpiData.rawScore}` },
                        ].map((stat, i) => (
                            <div key={i} className="bg-white p-8 rounded-[35px] shadow-[0_10px_30px_rgba(0,0,0,0.03)] border border-gray-100 flex items-center gap-6 hover:shadow-xl transition-all group">
                                <div className={`w-16 h-16 rounded-[22px] flex items-center justify-center text-3xl shadow-sm border border-white group-hover:scale-110 transition-transform ${stat.color}`}>
                                    {stat.icon}
                                </div>
                                <div className="flex-1">
                                    <p className="text-[10px] font-black tracking-widest text-gray-400 mb-1">{stat.label}</p>
                                    <div className="flex flex-col">
                                        <p className="text-3xl font-black text-gray-900 tracking-tighter tabular-nums">{stat.val}</p>
                                        {(stat.label === 'SORU HIZI' && kpiData.speedDiff) ? (
                                            <p className="text-[10px] font-bold text-emerald-500 mt-1 uppercase">{kpiData.speedDiff}</p>
                                        ) : stat.sub ? (
                                            <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase opacity-0 group-hover:opacity-100 transition-opacity">{stat.sub}</p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : mainTab === 'MISTAKE_REPORT' ? (
                renderMistakeReport()
            ) : mainTab === 'ANALYSIS' ? (
                <div className="space-y-8 min-h-[500px]">
                    {/* Header */}
                    <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-gray-100 dark:border-white/10 shadow-sm">
                        <h3 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-3 mb-2 tracking-tight">
                            <LightBulbIcon className="w-7 h-7 text-purple-500" />
                            Kişiselleştirilmiş Çalışma Önerileri
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 max-w-2xl text-sm">
                            Deneme sonuçların ve ÖSYM'nin son 7 yıldaki soru dağılımları analiz edilerek oluşturulmuştur.
                            Bu konulara öncelik vermen netlerini hızla artıracaktır.
                        </p>
                    </div>

                    {recommendations.length > 0 ? (
                        <>
                            {/* 🔴 Birincil Öncelikli */}
                            {recommendations.filter(r => r.priority === 1).length > 0 && (
                                <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-red-100 dark:border-red-500/20 shadow-sm overflow-hidden">
                                    <div className="bg-gradient-to-r from-red-600 to-rose-600 px-8 py-5 flex items-center gap-3">
                                        <span className="text-2xl">🔴</span>
                                        <div>
                                            <h3 className="text-lg font-black text-white tracking-tight">Birincil Öncelikli Halletmen Gereken Konular</h3>
                                            <p className="text-red-100 text-xs font-bold">ACİL — Bu konularda sürekli yanlış yapıyorsun</p>
                                        </div>
                                        <span className="ml-auto bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-white text-xs font-black">
                                            {recommendations.filter(r => r.priority === 1).length} konu
                                        </span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="border-b border-red-50 dark:border-red-500/10">
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Ders</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Konu</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Yanlış Sayısı</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Deneme #</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Çıkma Oranı</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                                                {recommendations.filter(r => r.priority === 1).map(rec => (
                                                    <tr key={rec.id} className="hover:bg-red-50/50 dark:hover:bg-red-500/5 transition-colors group">
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-700 dark:text-gray-300">{rec.lesson}</td>
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-white">{rec.topic}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 text-sm font-black">{rec.mistakeCount}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-500">{rec.examsMistakenIn || '-'}</td>
                                                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-500">{(rec.annualAverage || 0).toFixed(1)}/yıl</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="px-3 py-1 rounded-full text-[10px] font-black bg-red-500 text-white shadow-sm">🔴 Kritik</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* 🟠 İkincil Öncelikli */}
                            {recommendations.filter(r => r.priority === 2).length > 0 && (
                                <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-orange-100 dark:border-orange-500/20 shadow-sm overflow-hidden">
                                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-5 flex items-center gap-3">
                                        <span className="text-2xl">🟠</span>
                                        <div>
                                            <h3 className="text-lg font-black text-white tracking-tight">İkincil Öncelikli Halletmen Gereken Konular</h3>
                                            <p className="text-orange-100 text-xs font-bold">ÖNEMLİ — Bu konulara dikkat etmelisin</p>
                                        </div>
                                        <span className="ml-auto bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-white text-xs font-black">
                                            {recommendations.filter(r => r.priority === 2).length} konu
                                        </span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="border-b border-orange-50 dark:border-orange-500/10">
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Ders</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Konu</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Yanlış Sayısı</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Deneme #</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Çıkma Oranı</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                                                {recommendations.filter(r => r.priority === 2).map(rec => (
                                                    <tr key={rec.id} className="hover:bg-orange-50/50 dark:hover:bg-orange-500/5 transition-colors group">
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-700 dark:text-gray-300">{rec.lesson}</td>
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-white">{rec.topic}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-sm font-black">{rec.mistakeCount}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-500">{rec.examsMistakenIn || '-'}</td>
                                                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-500">{(rec.annualAverage || 0).toFixed(1)}/yıl</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="px-3 py-1 rounded-full text-[10px] font-black bg-orange-500 text-white shadow-sm">🟠 Önemli</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* 🔵 Üçüncül Öncelikli */}
                            {recommendations.filter(r => r.priority === 3).length > 0 && (
                                <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-100 dark:border-blue-500/20 shadow-sm overflow-hidden">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-5 flex items-center gap-3">
                                        <span className="text-2xl">🔵</span>
                                        <div>
                                            <h3 className="text-lg font-black text-white tracking-tight">Üçüncül Öncelikli Halletmen Gereken Konular</h3>
                                            <p className="text-blue-100 text-xs font-bold">TEKRAR — Bu konuları tekrar etmen faydalı olur</p>
                                        </div>
                                        <span className="ml-auto bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-white text-xs font-black">
                                            {recommendations.filter(r => r.priority === 3).length} konu
                                        </span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="border-b border-blue-50 dark:border-blue-500/10">
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Ders</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Konu</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Yanlış Sayısı</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Deneme #</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Çıkma Oranı</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                                                {recommendations.filter(r => r.priority === 3).map(rec => (
                                                    <tr key={rec.id} className="hover:bg-blue-50/50 dark:hover:bg-blue-500/5 transition-colors group">
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-700 dark:text-gray-300">{rec.lesson}</td>
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-white">{rec.topic}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-black">{rec.mistakeCount}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-500">{rec.examsMistakenIn || '-'}</td>
                                                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-500">{(rec.annualAverage || 0).toFixed(1)}/yıl</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="px-3 py-1 rounded-full text-[10px] font-black bg-blue-500 text-white shadow-sm">🔵 Tekrar</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-gray-100 dark:border-white/10 shadow-sm flex flex-col items-center justify-center h-[300px] text-center p-8">
                            <CheckCircleIcon className="w-16 h-16 text-green-200 mb-4" />
                            <h4 className="text-lg font-bold text-gray-800 dark:text-white">Harika Gidiyorsun!</h4>
                            <p className="text-gray-500 dark:text-gray-400 max-w-md">Çözdüğün denemelerde şu an için kritik bir eksik tespit edilemedi. Deneme çözmeye devam et!</p>
                        </div>
                    )}
                </div>
            ) : mainTab === 'QUESTION_BANK' ? (
                <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 p-8 rounded-3xl shadow-lg text-white relative overflow-hidden min-h-[400px]">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-bl-full -mr-20 -mt-20" />
                    <h3 className="text-2xl font-bold mb-4 flex items-center gap-3 relative z-10">
                        <BookOpenIcon className="w-8 h-8 text-blue-300" /> Kişisel Soru Bankam
                    </h3>
                    <p className="text-gray-300 text-sm mb-8 relative z-10 max-w-2xl">
                        Yapamadığın konulardan senin için seçilmiş özel sorular. Her deneme sonrası eksik konuların buraya otomatik olarak eklenir.
                    </p>

                    {/* Hata Kitapçığı Oluştur Button */}
                    {mergedQuestionSets.length > 0 && (
                        <div className="mb-8 relative z-10">
                            <button
                                onClick={() => setShowBookletModal(true)}
                                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl font-black text-sm hover:from-indigo-600 hover:to-purple-700 transition-all shadow-xl shadow-indigo-500/30 active:scale-95"
                            >
                                <DocumentTextIcon className="w-5 h-5" />
                                Hata Kitapçığı Oluştur
                                <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-xs">
                                    {mergedQuestionSets.reduce((acc: number, s: any) => acc + (s.count || 0), 0)} Soru
                                </span>
                            </button>
                        </div>
                    )}

                    {personalQuestionSets.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                            {personalQuestionSets.map((set, idx) => (
                                <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 rounded-2xl flex flex-col hover:bg-white/10 transition-all cursor-pointer shadow-lg" onClick={() => startQuiz(set)}>
                                    <div className="flex-1 mb-4">
                                        <p className="font-bold text-white text-lg">{set.topic}</p>
                                        <p className="text-sm text-gray-400 mt-1">{set.subject}</p>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs bg-white/10 px-3 py-1 rounded-full text-blue-200">{set.count} Soru</span>
                                        <button className="px-5 py-2 bg-white text-indigo-900 rounded-xl font-bold text-sm hover:bg-blue-50 transition-colors shadow-md">
                                            Çöz
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 text-gray-400 relative z-10">
                            <CheckCircleIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <h4 className="text-xl font-bold text-white mb-2">Harikasın! 🎉</h4>
                            <p className="text-gray-400 max-w-md mx-auto">Şu an için eksik konun bulunmuyor. Deneme çözdükçe yapamadığın konular buraya eklenecek.</p>
                        </div>
                    )}
                </div>
            ) : null}

            {/* Add Exam Modal */}
            {showAddModal && (
                <AddExamModal
                    type={subTab}
                    libraryExam={selectedLibraryExam}
                    onClose={() => setShowAddModal(false)}
                    onSave={handleSaveExam}
                />
            )}

            {/* Personal Quiz Modal */}
            {showQuizModal && quizQuestions.length > 0 && (
                <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl animate-scale-in">
                        <div className="bg-gray-50 p-4 border-b flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-gray-800">{quizTitle}</h3>
                                <p className="text-xs text-gray-500">Soru {activeQuestionIndex + 1} / {quizQuestions.length}</p>
                            </div>
                            <button onClick={() => setShowQuizModal(false)} title="Kapat" className="text-gray-400 hover:text-gray-600"><XCircleIcon className="w-8 h-8" /></button>
                        </div>
                        <div className="p-8 max-h-[60vh] overflow-y-auto">
                            {quizQuestions[activeQuestionIndex].image && (
                                <div className="mb-6 rounded-2xl overflow-hidden border border-gray-100 shadow-inner bg-gray-50 flex items-center justify-center p-4">
                                    <img src={quizQuestions[activeQuestionIndex].image} alt="Soru" className="max-w-full max-h-[300px] object-contain" />
                                </div>
                            )}

                            <p className="text-lg font-medium text-gray-800 mb-8 leading-relaxed">
                                {quizQuestions[activeQuestionIndex].text}
                            </p>

                            <div className="space-y-3">
                                {quizQuestions[activeQuestionIndex].options.map((opt: string, i: number) => (
                                    <button
                                        key={i}
                                        onClick={() => handleAnswer(i)}
                                        disabled={showFeedback}
                                        className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all relative ${showFeedback
                                            ? i === quizQuestions[activeQuestionIndex].correct
                                                ? 'bg-green-50 border-green-500 text-green-700'
                                                : i === selectedOption
                                                    ? 'bg-red-50 border-red-500 text-red-700'
                                                    : 'bg-white border-gray-100 text-gray-400'
                                            : selectedOption === i
                                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                                : 'bg-white border-gray-200 hover:border-indigo-300 text-gray-700'
                                            }`}
                                    >
                                        <span className="font-bold mr-3">{String.fromCharCode(65 + i)})</span> {opt}
                                        {showFeedback && i === quizQuestions[activeQuestionIndex].correct && <CheckCircleIcon className="w-6 h-6 absolute right-4 top-1/2 -translate-y-1/2 text-green-500" />}
                                        {showFeedback && i === selectedOption && i !== quizQuestions[activeQuestionIndex].correct && <XCircleIcon className="w-6 h-6 absolute right-4 top-1/2 -translate-y-1/2 text-red-500" />}
                                    </button>
                                ))}
                            </div>

                            {/* Solution Section */}
                            {showFeedback && (quizQuestions[activeQuestionIndex].solutionImage || quizQuestions[activeQuestionIndex].solutionVideoId) && (
                                <div className="mt-8 p-6 bg-indigo-50 rounded-2xl border border-indigo-100 animate-slide-up">
                                    <h4 className="font-black text-indigo-900 text-sm mb-4 flex items-center gap-2">
                                        <LightBulbIcon className="w-4 h-4" /> Çözümü Gör
                                    </h4>
                                    {quizQuestions[activeQuestionIndex].solutionImage && (
                                        <div className="rounded-xl overflow-hidden border border-indigo-200 mb-4 bg-white">
                                            <img src={quizQuestions[activeQuestionIndex].solutionImage} alt="Çözüm" className="w-full h-auto" />
                                        </div>
                                    )}
                                    {quizQuestions[activeQuestionIndex].solutionVideoId && (
                                        <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                                            <iframe
                                                width="100%"
                                                height="100%"
                                                src={youtubeGommeAdresi(quizQuestions[activeQuestionIndex].solutionVideoId)}
                                                title="YouTube video player"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="p-4 bg-gray-50 border-t flex justify-end">
                            {showFeedback && (
                                <button onClick={nextQuestion} className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg">
                                    {activeQuestionIndex < quizQuestions.length - 1 ? 'Sonraki Soru' : 'Bitir'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Booklet Modal */}
            {renderBookletModal()}
        </div>
    );
};
